// Ledger → Procurement view (rebuilt 2026-05-25).
//
// Project-scoped financial ledger grouped by proposal. Each proposal
// renders as a bounded card with item rows beneath. The dollar amount
// in each lifecycle cell IS the visual anchor — supporting detail
// (document ref, dates, QB id, push button) hides behind a hover
// tooltip. Modeled on Linear / Stripe data-table conventions.
//
// Layout:
//   - Required project selector — no default "All projects" view
//   - Status filter: ≥ Client Approved (financially active items only)
//   - Proposals ordered by approved_at desc
//   - 9 columns (down from 13 — three pairs of nearly-redundant cols
//     merged):
//       PRODUCT (SKU + Item) | DEPOSIT | PO | FINAL | VENDOR FINAL |
//       DELIVERED | FEES | RECEIVABLE | PAYABLE
//   - Sticky-left: PRODUCT; sticky-right: RECEIVABLE + PAYABLE
//   - Each lifecycle cell shows ONE dollar amount with a color cue
//     telling the story (green=paid, amber=partial, fuchsia=unpaid,
//     muted=projected). For partial pay the amount stacks (paid /
//     of expected). Tiny ✓ when the underlying record is in QB
//   - Hover any lifecycle cell to see the supporting detail
//   - Proposal cards: hairline border + rounded corners; banner row
//     spans the card top; items table fills the remainder
//
// Per-item math: each lifecycle cell shows THIS item's specific dollar
// share via procurementRowState (utils.js). Pro-rata patterns
// documented per-helper.

import React, { useMemo } from 'react';
import { useAppContext } from '../AppContext';
import {
  procurementRowState, isBillable, isFinanciallyCommitted, parseQty,
  itemTotalProfit,
  itemFeeMarkupPct, customFeeMarkupPct,
} from '../../utils';
import { jumpTo } from '../../lib/jump';
import { useHashFilters } from '../../lib/useHashFilters';
import LedgerCoBadge from './LedgerCoBadge';
import LedgerHoverTooltip from './LedgerHoverTooltip';
import {
  // Constants
  GROUP_BG, HEADER_BG, ROW_BG_A, ROW_BG_B, FOOTER_BG, CARD_BORDER, CARD_RADIUS,
  W, td, th, thRight,
  // Formatters / layout helpers
  fmt$, fmtShortDate, cellWidth, stickyLeft, stickyRight, ColGroup,
  // Components
  TooltipHeading, TooltipRow, AmountCell, FeesCell, PaymentBlocks,
  // Classifiers
  depositState, finalState, poState, venFinalState, statusLabel,
  // Payment-block lookups
  clientPaymentBlocksFor, vendorPaymentBlocksFor,
} from './LedgerShared';

// Compute ISO date bounds for the proc.date hash filter values. Returns
// null for the no-filter case ('' / all time) so the caller can skip
// the filter entirely. Bounds are inclusive on both ends.
function dateRangeFor(value) {
  if (!value) return null;
  var now = new Date();
  function iso(d) {
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var dd = String(d.getDate()).padStart(2, '0');
    return d.getFullYear() + '-' + m + '-' + dd;
  }
  if (value === 'thismonth') {
    return { start: iso(new Date(now.getFullYear(), now.getMonth(), 1)), end: null };
  }
  if (value === 'lastmonth') {
    var lmStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    var lmEnd   = new Date(now.getFullYear(), now.getMonth(), 0); // day 0 of next month = last day of prev
    return { start: iso(lmStart), end: iso(lmEnd) };
  }
  if (value === 'last6m') {
    return { start: iso(new Date(now.getFullYear(), now.getMonth() - 6, now.getDate())), end: null };
  }
  if (value === 'lastyear') {
    return { start: iso(new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())), end: null };
  }
  return null;
}

// All of: constants (HEADER_BG / GROUP_BG / etc), W, td/th/thRight,
// fmt$, fmtShortDate, cellWidth, stickyLeft, stickyRight, ColGroup,
// AmountCell, FeesCell, PaymentBlocks, classifiers (depositState /
// finalState / poState / venFinalState), statusLabel, and the
// client/vendor payment block lookups live in LedgerShared.jsx. The
// Billing Summaries view consumes the same primitives.

export default function LedgerProcurement(p) {
  var ctx = useAppContext();
  var firmSettings = p.firmSettings || ctx.firmSettings || {};

  // ── Source data ───────────────────────────────────────────────────────
  var items              = ctx.items              || [];
  var proposals          = ctx.proposals          || [];
  var invoices           = ctx.invoices           || [];
  var paymentAllocations = ctx.paymentAllocations || [];
  var vendorPayments     = ctx.vendorPayments     || [];
  var purchaseOrders     = ctx.purchaseOrders     || [];
  var clientPayments     = ctx.clientPayments     || [];
  var changeOrders       = ctx.changeOrders       || [];
  var projects           = ctx.projects           || [];

  var setProposals      = ctx.setProposals;
  var setInvoices       = ctx.setInvoices;
  var setPurchaseOrders = ctx.setPurchaseOrders;

  // ── Filters (URL hash persisted; namespace 'proc') ────────────────────
  // Date filter values match the hash filter encoding:
  //   ''           — all time (default)
  //   'thismonth'  — current calendar month
  //   'lastmonth'  — previous calendar month
  //   'last6m'     — sliding 6-month window
  //   'lastyear'   — sliding 365-day window
  var f = useHashFilters({
    namespace: 'proc',
    defaults: { proj: '', q: '', unpaid: false, date: '' },
  });
  var filters = f[0]; var setFilter = f[2];
  var projFlt    = filters.proj;
  var query      = filters.q;
  var unpaidOnly = filters.unpaid;
  var dateFlt    = filters.date;

  var allProjectLabels = useMemo(function() {
    var labels = projects.map(function(pr) { return pr.label; }).filter(Boolean);
    return labels.sort();
  }, [projects]);

  var rowCtx = useMemo(function() {
    return {
      items: items, proposals: proposals, invoices: invoices,
      paymentAllocations: paymentAllocations, vendorPayments: vendorPayments,
      purchaseOrders: purchaseOrders, payments: clientPayments,
      changeOrders: changeOrders,
    };
  }, [items, proposals, invoices, paymentAllocations, vendorPayments, purchaseOrders, clientPayments, changeOrders]);

  var rows = useMemo(function() {
    if (!projFlt) return [];
    var q = (query || '').trim().toLowerCase();
    return items
      .filter(function(it) {
        if (!it || it.parentId) return false;
        if (!isBillable(it)) return false;
        if (!isFinanciallyCommitted(it.status)) return false;
        if ((it.projectLabel || it.project) !== projFlt) return false;
        var src = (it.importSource || '').toLowerCase();
        if (src.indexOf('abd') >= 0 || src.indexOf('pre-app') >= 0) return false;
        return true;
      })
      .map(function(it) { return { item: it, state: procurementRowState(it, rowCtx) }; })
      .filter(function(r) {
        var st = r.state;
        if (unpaidOnly && (st.clientOutstanding || 0) <= 0 && (st.vendorOutstanding || 0) <= 0) return false;
        if (!q) return true;
        var it = r.item;
        var hay = [
          it.itemNumber, it.productName, it.item, it.vendor,
          st.po && st.po.ref,
          st.depositInvoice && st.depositInvoice.invoiceRef,
          st.finalInvoice && st.finalInvoice.invoiceRef,
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.indexOf(q) >= 0;
      });
  }, [items, rowCtx, projFlt, query, unpaidOnly]);

  var groups = useMemo(function() {
    var byProposal = {};
    var noPropRows = [];
    rows.forEach(function(r) {
      var pid = r.item.proposalId || '';
      if (!pid) { noPropRows.push(r); return; }
      if (!byProposal[pid]) byProposal[pid] = [];
      byProposal[pid].push(r);
    });
    var groupList = Object.keys(byProposal).map(function(pid) {
      var proposal = proposals.find(function(pp) { return pp.id === pid; });
      var groupRows = byProposal[pid];
      var net = 0, clientTotal = 0, depExpected = 0, depPaid = 0, outstanding = 0, profit = 0;
      groupRows.forEach(function(r) {
        net         += (r.state.net || 0);
        clientTotal += (r.state.clientTotal || 0);
        depExpected += (r.state.itemDepositExpected || 0);
        depPaid     += (r.state.itemDepositPaid || 0);
        outstanding += (r.state.clientOutstanding || 0);
        profit      += parseFloat(itemTotalProfit(r.item, items)) || 0;
      });
      return {
        proposal: proposal, proposalId: pid, rows: groupRows,
        approvedAt: (proposal && (proposal.approved_at || proposal.approvedAt)) || '',
        ref:        (proposal && (proposal.proposal_ref || proposal.proposalRef)) || pid,
        name:       (proposal && (proposal.proposal_name || proposal.proposalName || proposal.name)) || '',
        totals: {
          net: Math.round(net * 100) / 100,
          clientTotal: Math.round(clientTotal * 100) / 100,
          depExpected: Math.round(depExpected * 100) / 100,
          depPaid: Math.round(depPaid * 100) / 100,
          outstanding: Math.round(outstanding * 100) / 100,
          profit: Math.round(profit * 100) / 100,
        },
      };
    });
    // Apply date filter on proposal.approved_at. Unapproved groups
    // drop when a range is active (they have no date to test against).
    var range = dateRangeFor(dateFlt);
    if (range) {
      groupList = groupList.filter(function(g) {
        var d = String(g.approvedAt || '').slice(0, 10);
        if (!d) return false;
        if (range.start && d < range.start) return false;
        if (range.end && d > range.end) return false;
        return true;
      });
    }
    groupList.sort(function(a, b) {
      if (!a.approvedAt && !b.approvedAt) return 0;
      if (!a.approvedAt) return 1;
      if (!b.approvedAt) return -1;
      return String(b.approvedAt).localeCompare(String(a.approvedAt));
    });
    // Orphan items (no proposal). Skip when a date filter is active —
    // there's no proposal date to satisfy the range.
    if (noPropRows.length && !range) {
      groupList.push({
        proposal: null, proposalId: '', rows: noPropRows,
        ref: '(no proposal)', name: '', approvedAt: '',
        totals: { net: 0, clientTotal: 0, depExpected: 0, depPaid: 0, outstanding: 0 },
      });
    }
    return groupList;
  }, [rows, proposals, dateFlt]);

  // Totals scoped to the date-filtered groups (not raw rows) so the
  // floating bottom bar reflects exactly what the user sees on screen.
  var totals = useMemo(function() {
    var clientOut = 0, vendorOut = 0, net = 0, clientTotal = 0, profit = 0;
    groups.forEach(function(g) {
      g.rows.forEach(function(r) {
        clientOut   += (r.state.clientOutstanding || 0);
        vendorOut   += (r.state.vendorOutstanding || 0);
        net         += (r.state.net || 0);
        clientTotal += (r.state.clientTotal || 0);
        // Per-item profit derives from utils (item margin + fee margin)
        profit += parseFloat(itemTotalProfit(r.item, items)) || 0;
      });
    });
    var rnd = function(n) { return Math.round(n * 100) / 100; };
    return {
      clientOutstanding: rnd(clientOut), vendorOutstanding: rnd(vendorOut),
      net: rnd(net), clientTotal: rnd(clientTotal), profit: rnd(profit),
    };
  }, [groups, items]);

  // ── Column widths ─────────────────────────────────────────────────────
  var W = {
    product: 240,
    deposit: 140, po: 130, final: 140, venFinal: 120,
    delivered: 100, fees: 110,
    cliOut: 110, venOut: 110,
  };
  function cellWidth(w) { return { minWidth: w, maxWidth: w, width: w }; }
  function stickyLeft(offset, bg)  { return { position: 'sticky', left: offset,  background: bg || STICKY_BG, zIndex: 1 }; }
  function stickyRight(offset, bg) { return { position: 'sticky', right: offset, background: bg || STICKY_BG, zIndex: 1 }; }

  // Bind the shared payment-block lookups to this view's context arrays.
  // Local closures so call sites stay simple (documentType, documentId).
  function clientPaymentBlocks(documentType, documentId) {
    return clientPaymentBlocksFor(documentType, documentId, paymentAllocations, clientPayments);
  }
  function vendorPaymentBlocks(poId, paymentType) {
    return vendorPaymentBlocksFor(poId, paymentType, vendorPayments);
  }

  // ── Empty state: project required ─────────────────────────────────────
  if (!projFlt) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span style={{ fontSize: 11, color: '#555', fontWeight: 500 }}>Project:</span>
          <select value={projFlt} onChange={function(e) { setFilter('proj', e.target.value); }}
            style={{ padding: '6px 12px', fontSize: 12, fontFamily: 'inherit',
              border: '0.5px solid #111', borderRadius: 4, background: '#fff', minWidth: 220 }}>
            <option value="">— Choose a project —</option>
            {allProjectLabels.map(function(lab) { return <option key={lab} value={lab}>{lab}</option>; })}
          </select>
        </div>
        <div style={{ padding: 60, textAlign: 'center', color: '#888',
          border: '0.5px dashed #D8D7D2', borderRadius: 6, background: '#FAFAF8' }}>
          <div style={{ fontSize: 14, color: '#555', marginBottom: 6 }}>Select a project</div>
          <div style={{ fontSize: 11 }}>Choose a project above to view its financial records.</div>
        </div>
      </div>
    );
  }

  // ColGroup imported from LedgerShared — same column widths across
  // header, per-proposal item tables, and footer in both views.

  // Sticky-top header. zIndex layering: outer wrapper sits at 5 so it
  // covers the proposal cards as they scroll under it. Sticky-left and
  // sticky-right corner cells layer at 6 — above the body's sticky
  // cells (z 1) so the corners stay on top during horizontal scroll.
  function HeaderRow() {
    return (
      <div style={{ position: 'sticky', top: 0, zIndex: 5, background: HEADER_BG }}>
        <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: 'max-content' }}>
          <ColGroup />
          <thead>
            <tr>
              <th style={Object.assign({}, th, cellWidth(W.product), stickyLeft(0, HEADER_BG), { zIndex: 6 })}>Product</th>
              <th style={Object.assign({}, thRight, cellWidth(W.deposit))}>Client Deposit</th>
              <th style={Object.assign({}, thRight, cellWidth(W.final))}>Client Final</th>
              <th style={Object.assign({}, thRight, cellWidth(W.po))}>Vendor PO</th>
              <th style={Object.assign({}, thRight, cellWidth(W.venFinal))}>Vendor Final</th>
              <th style={Object.assign({}, th, cellWidth(W.delivered))}>Delivered</th>
              <th style={Object.assign({}, thRight, cellWidth(W.fees))}>Fees</th>
              <th style={Object.assign({}, thRight, cellWidth(W.cliOut), stickyRight(W.venOut, HEADER_BG), { zIndex: 6 })}>
                <div>Receivable</div>
                <div style={{ fontSize: 8, fontWeight: 400, color: '#aaa',
                  textTransform: 'none', letterSpacing: 0, marginTop: 1 }}>(client owes)</div>
              </th>
              <th style={Object.assign({}, thRight, cellWidth(W.venOut), stickyRight(0, HEADER_BG), { zIndex: 6 })}>
                <div>Payable</div>
                <div style={{ fontSize: 8, fontWeight: 400, color: '#aaa',
                  textTransform: 'none', letterSpacing: 0, marginTop: 1 }}>(owe vendor)</div>
              </th>
            </tr>
          </thead>
        </table>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: '#555', fontWeight: 500 }}>Project:</span>
        <select value={projFlt} onChange={function(e) { setFilter('proj', e.target.value); }}
          style={{ padding: '6px 12px', fontSize: 12, fontFamily: 'inherit',
            border: '0.5px solid #111', borderRadius: 4, background: '#fff', minWidth: 200 }}>
          <option value="">— Choose a project —</option>
          {allProjectLabels.map(function(lab) { return <option key={lab} value={lab}>{lab}</option>; })}
        </select>
        <select value={dateFlt} onChange={function(e) { setFilter('date', e.target.value); }}
          title="Filter by proposal approved date"
          style={{ padding: '6px 10px', fontSize: 11, fontFamily: 'inherit',
            border: '0.5px solid #D8D7D2', borderRadius: 4, background: '#fff' }}>
          <option value="">All time</option>
          <option value="thismonth">Current month</option>
          <option value="lastmonth">Last month</option>
          <option value="last6m">Last 6 months</option>
          <option value="lastyear">Last year</option>
        </select>
        <input type="text" placeholder="Search SKU, item, vendor, invoice ref…"
          value={query} onChange={function(e) { setFilter('q', e.target.value); }}
          style={{ flex: 1, minWidth: 200, padding: '6px 10px', fontSize: 11, fontFamily: 'inherit',
            border: '0.5px solid #D8D7D2', borderRadius: 4, background: '#fff' }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#555', cursor: 'pointer' }}
          title="Hide rows where both Receivable and Payable are zero (fully settled).">
          <input type="checkbox" checked={unpaidOnly} onChange={function(e) { setFilter('unpaid', e.target.checked); }} />
          Unpaid balance only
        </label>
        <span style={{ fontSize: 11, color: '#888', marginLeft: 'auto' }}>
          {rows.length} {rows.length === 1 ? 'item' : 'items'}
          {groups.length ? ' in ' + groups.length + ' proposal' + (groups.length === 1 ? '' : 's') : ''}
        </span>
      </div>

      {/* Scroll container — sticky-left/right anchored to its edges */}
      <div style={{
        border: '0.5px solid #E0E0DC', borderRadius: 6, overflow: 'auto', maxHeight: '72vh',
        background: '#fff',
      }}>
        <HeaderRow />

        {/* Empty state inside the scroll container */}
        {groups.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: '#888', fontSize: 12 }}>
            No financially active items for this project.
            <div style={{ marginTop: 4, fontSize: 10, color: '#aaa' }}>
              Items appear after the proposal is approved (status ≥ Client Approved).
            </div>
          </div>
        )}

        {/* One card per proposal */}
        {groups.map(function(g) {
          return (
            <div key={g.proposalId || 'no-prop'} style={{
              // Vertical-only margin. Horizontal margin would inset the
              // card by 14px from the scroll container edge, drifting
              // its columns out of alignment with the sticky header and
              // footer tables (which sit at the container edge).
              marginTop: 14,
              border: CARD_BORDER, borderRadius: CARD_RADIUS,
              background: '#fff',
            }}>
              {/* Banner */}
              <div style={{
                background: GROUP_BG,
                borderTopLeftRadius: CARD_RADIUS, borderTopRightRadius: CARD_RADIUS,
                borderBottom: '0.5px solid #D8D7D2',
                padding: '10px 14px',
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
                  fontSize: 12, lineHeight: 1.4 }}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#111',
                    cursor: g.proposalId ? 'pointer' : 'default' }}
                    onClick={g.proposalId ? function() { jumpTo('proposals', g.proposalId); } : null}>
                    {g.ref}
                  </span>
                  {g.name && (
                    <>
                      <span style={{ color: '#aaa' }}>{'\u00b7'}</span>
                      <span style={{ color: '#222' }}>{g.name}</span>
                    </>
                  )}
                  {g.approvedAt && (
                    <>
                      <span style={{ color: '#aaa' }}>{'\u00b7'}</span>
                      <span style={{ fontSize: 10, color: '#1A5C38' }}>Approved {fmtShortDate(g.approvedAt)}</span>
                    </>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 14, marginTop: 4, fontSize: 10, color: '#555',
                  flexWrap: 'wrap' }}>
                  <span>Net <span style={{ fontFamily: 'monospace', color: '#111' }}>{fmt$(g.totals.net)}</span></span>
                  <span>Client <span style={{ fontFamily: 'monospace', color: '#111' }}>{fmt$(g.totals.clientTotal)}</span></span>
                  <span>Profit <span style={{ fontFamily: 'monospace', color: '#1A5C38' }}>{fmt$(g.totals.profit)}</span><span style={{ color: '#aaa' }}>{' (' + (g.totals.net > 0 ? Math.round((g.totals.profit / g.totals.net) * 100) : 0) + '%)'}</span></span>
                  <span>Dep collected <span style={{ fontFamily: 'monospace', color: '#1A5C38' }}>{fmt$(g.totals.depPaid)}</span><span style={{ color: '#aaa' }}> / {fmt$(g.totals.depExpected)}</span></span>
                  <span>Outstanding <span style={{ fontFamily: 'monospace',
                    color: g.totals.outstanding > 0 ? '#D4007A' : '#888' }}>{fmt$(g.totals.outstanding)}</span></span>
                </div>
              </div>

              {/* Items table — colgroup matches HeaderRow above */}
              <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: 'max-content' }}>
                <ColGroup />
                <tbody>
                  {g.rows.map(function(r, ri, arr) {
                    var it = r.item; var st = r.state;
                    var isLast = ri === arr.length - 1;
                    var rowBg = ri % 2 === 0 ? ROW_BG_A : ROW_BG_B;
                    var firstCellRadius  = isLast ? { borderBottomLeftRadius:  CARD_RADIUS } : null;
                    var lastCellRadius   = isLast ? { borderBottomRightRadius: CARD_RADIUS } : null;

                    // Compute per-cell state
                    var depSt = depositState(st);
                    var finSt = finalState(st);
                    var poSt  = poState(st, it);
                    var venSt = venFinalState(st);

                    // ── DEPOSIT cell content + tooltip ────────────────
                    var depAmount = null; var depPartialOf = null;
                    if (depSt === 'paid')      { depAmount = st.itemDepositPaid; }
                    else if (depSt === 'partial') { depAmount = st.itemDepositPaid; depPartialOf = st.itemDepositExpected; }
                    else if (depSt === 'unpaid' || depSt === 'projected') { depAmount = st.itemDepositExpected; }
                    var depPaymentBlocks = st.depositInvoice
                      ? clientPaymentBlocks('invoice', st.depositInvoice.invoice.id)
                      : [];
                    var depTooltip = st.depositInvoice ? (
                      <div>
                        <TooltipHeading>Deposit Invoice</TooltipHeading>
                        <TooltipRow label="Ref"  mono value={st.depositInvoice.invoiceRef || '\u2014'}
                          onClick={function() { jumpTo('invoices', st.depositInvoice.invoice.id); }} />
                        {st.depositInvoice.sentAt && <TooltipRow label="Sent" value={fmtShortDate(st.depositInvoice.sentAt)} />}
                        <TooltipRow label="Client deposit (req'd)" mono value={fmt$(st.itemDepositExpected)} />
                        {st.itemDepositPaid > 0 && (
                          <TooltipRow label="Client deposit paid" mono value={fmt$(st.itemDepositPaid)} />
                        )}
                        <TooltipRow label="Status" value={statusLabel(depSt)} />
                        {PaymentBlocks(depPaymentBlocks)}
                      </div>
                    ) : (st.itemDepositExpected > 0.01 ? (
                      <div>
                        <TooltipHeading>Deposit (Projected)</TooltipHeading>
                        <TooltipRow label="Projected" mono value={fmt$(st.itemDepositExpected)} />
                        <div style={{ marginTop: 6, fontSize: 10, color: '#888', fontStyle: 'italic' }}>
                          Deposit invoice not yet generated for this proposal.
                        </div>
                      </div>
                    ) : null);

                    // ── PO cell content + tooltip ─────────────────────
                    var poAmount = st.po ? st.net : null;
                    // Vendor deposit "expected" for this item — used to label the row
                    var venDepExpected = st.po
                      ? Math.round((parseFloat(it.netPrice) || 0) * (parseQty(it.qty) || 1) * (parseFloat(it.depositPct) || 50) / 100 * 100) / 100
                      : 0;
                    var poDepositBlocks = st.po ? vendorPaymentBlocks(st.po.id, 'Deposit') : [];
                    // Legacy "Deposit Payment" + "Full" buckets also count
                    var poDepositBlocksLegacy = st.po
                      ? vendorPaymentBlocks(st.po.id).filter(function(b) {
                          return b.paymentType === 'Deposit Payment' || b.paymentType === 'Full';
                        })
                      : [];
                    var allPoDepositBlocks = poDepositBlocks.concat(poDepositBlocksLegacy);
                    var poTooltip = st.po ? (
                      <div>
                        <TooltipHeading>Purchase Order</TooltipHeading>
                        <TooltipRow label="Ref"  mono value={st.po.ref || '\u2014'}
                          onClick={function() { jumpTo('purchase-orders', st.po.id); }} />
                        {st.po.sentAt && <TooltipRow label="Issued" value={fmtShortDate(st.po.sentAt)} />}
                        <TooltipRow label="Item net" mono value={fmt$(st.net)} />
                        {venDepExpected > 0 && (
                          <TooltipRow label="Vendor deposit (req'd)" mono value={fmt$(venDepExpected)} />
                        )}
                        {st.itemVendorDepositPaid > 0 && (
                          <TooltipRow label="Vendor deposit paid" mono value={fmt$(st.itemVendorDepositPaid)} />
                        )}
                        <TooltipRow label="Status" value={statusLabel(poSt)} />
                        {PaymentBlocks(allPoDepositBlocks)}
                      </div>
                    ) : null;

                    // ── FINAL cell content + tooltip ──────────────────
                    var finAmount = null; var finPartialOf = null;
                    if (finSt === 'paid')      { finAmount = st.itemFinalPaid; }
                    else if (finSt === 'partial') { finAmount = st.itemFinalPaid; finPartialOf = st.itemFinalExpected; }
                    else if (finSt === 'unpaid' || finSt === 'projected') { finAmount = st.itemFinalExpected; }
                    var finPaymentBlocks = st.finalInvoice
                      ? clientPaymentBlocks('invoice', st.finalInvoice.invoice.id)
                      : [];
                    var finTooltip = st.finalInvoice ? (
                      <div>
                        <TooltipHeading>Final Invoice</TooltipHeading>
                        <TooltipRow label="Ref" mono value={st.finalInvoice.invoiceRef || '\u2014'}
                          onClick={function() { jumpTo('invoices', st.finalInvoice.invoice.id); }} />
                        {st.finalInvoice.sentAt && <TooltipRow label="Sent" value={fmtShortDate(st.finalInvoice.sentAt)} />}
                        <TooltipRow label="Client final (req'd)" mono value={fmt$(st.itemFinalExpected)} />
                        {st.itemFinalPaid > 0 && (
                          <TooltipRow label="Client final paid" mono value={fmt$(st.itemFinalPaid)} />
                        )}
                        <TooltipRow label="Status" value={statusLabel(finSt)} />
                        {PaymentBlocks(finPaymentBlocks)}
                      </div>
                    ) : (st.itemFinalExpected > 0.01 ? (
                      <div>
                        <TooltipHeading>Final (Projected)</TooltipHeading>
                        <TooltipRow label="Projected" mono value={fmt$(st.itemFinalExpected)} />
                        <div style={{ marginTop: 6, fontSize: 10, color: '#888', fontStyle: 'italic' }}>
                          Final invoice not yet generated.
                        </div>
                      </div>
                    ) : null);

                    // ── VENDOR FINAL cell content + tooltip ───────────
                    var venFinProjected = Math.max(0, Math.round(((st.net || 0) - (st.itemVendorDepositPaid || 0)) * 100) / 100);
                    var venFinAmount =
                      venSt === 'paid'      ? st.itemVendorFinalPaid :
                      venSt === 'projected' ? venFinProjected : null;
                    var venFinPaymentBlocks = st.po
                      ? vendorPaymentBlocks(st.po.id, 'Final')
                      : [];
                    var venFinTooltip;
                    if (venSt === 'paid') {
                      venFinTooltip = (
                        <div>
                          <TooltipHeading>Vendor Final Payment</TooltipHeading>
                          <TooltipRow label="Item share" mono value={fmt$(st.itemVendorFinalPaid)} />
                          {st.vendorPaymentStatus && st.vendorPaymentStatus.finalAt && (
                            <TooltipRow label="Date" value={fmtShortDate(st.vendorPaymentStatus.finalAt)} />
                          )}
                          {PaymentBlocks(venFinPaymentBlocks)}
                          <div style={{ marginTop: 6, fontSize: 10, color: '#888', fontStyle: 'italic' }}>
                            Push individual bill payments from the PO drilldown.
                          </div>
                        </div>
                      );
                    } else if (venSt === 'projected') {
                      venFinTooltip = (
                        <div>
                          <TooltipHeading>Vendor Final (Projected)</TooltipHeading>
                          <TooltipRow label="Item PO contribution" mono value={fmt$(st.net || 0)} />
                          <TooltipRow label="Vendor deposit paid" mono value={fmt$(st.itemVendorDepositPaid || 0)} />
                          <TooltipRow label="Projected final" mono value={fmt$(venFinProjected)} />
                          <div style={{ marginTop: 6, fontSize: 10, color: '#888', fontStyle: 'italic' }}>
                            No vendor final payment recorded yet. Projection = item PO contribution − vendor deposit paid.
                          </div>
                        </div>
                      );
                    } else {
                      venFinTooltip = null;
                    }

                    // Per-item profit breakdown for the PRODUCT cell tooltip.
                    var itClientTotal = (parseFloat(it.netPrice) || 0) * (parseQty(it.qty) || 1)
                      * (1 + (parseFloat(it.markupPct) || 0) / 100);
                    var itNet = (parseFloat(it.netPrice) || 0) * (parseQty(it.qty) || 1);
                    var itMargin = Math.round((itClientTotal - itNet) * 100) / 100;
                    var itMarginPct = itNet > 0 ? Math.round((itMargin / itNet) * 100) : 0;
                    var itFees = it.fees || {};
                    function feeMargin(raw, rate) {
                      var n = parseFloat(String(raw || '').replace(/[$,]/g, '')) || 0;
                      return Math.round(n * rate * 100) / 100;
                    }
                    var itFeeRate = itemFeeMarkupPct(it) / 100;
                    var itFeeMul  = (1 + itFeeRate).toFixed(2);
                    var freightMargin = feeMargin(itFees.freight, itFeeRate);
                    var cratingMargin = feeMargin(itFees.crating, itFeeRate);
                    var installMargin = feeMargin(itFees.installation, itFeeRate);
                    var customMarginTotal = (itFees.custom || []).reduce(function(s, c) {
                      return s + feeMargin(c.amount, customFeeMarkupPct(c, it) / 100);
                    }, 0);
                    var itTotalProfit = parseFloat(itemTotalProfit(it, items)) || 0;
                    var productTooltip = (
                      <div style={{ minWidth: 300 }}>
                        <TooltipHeading>Product</TooltipHeading>
                        <div style={{ fontSize: 12, color: '#111', fontWeight: 500, marginBottom: 2 }}>
                          {it.productName || it.item || '(unnamed)'}
                        </div>
                        <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#888', marginBottom: 8 }}>
                          SKU {it.itemNumber || '\u2014'}
                        </div>
                        <div style={{ fontSize: 9, fontWeight: 600, color: '#888',
                          textTransform: 'uppercase', letterSpacing: '0.07em',
                          paddingTop: 8, borderTop: '0.5px solid #E0E0DC', marginBottom: 6 }}>
                          Profit breakdown
                        </div>
                        <TooltipRow label="Item margin (net→client)" mono
                          value={fmt$(itMargin) + ' (' + itMarginPct + '%)'} />
                        {/* Literal × character in the label strings — JSX attribute
                            string literals don't process JS escape sequences, so the
                            "\u00d7" form rendered as 6 literal chars in earlier commits. */}
                        {freightMargin > 0 && <TooltipRow label={"Freight margin (×"+itFeeMul+")"} mono value={fmt$(freightMargin)} />}
                        {cratingMargin > 0 && <TooltipRow label={"Crating margin (×"+itFeeMul+")"} mono value={fmt$(cratingMargin)} />}
                        {installMargin > 0 && <TooltipRow label={"Installation margin (×"+itFeeMul+")"} mono value={fmt$(installMargin)} />}
                        {customMarginTotal > 0 && <TooltipRow label="Custom margin (per-row %)" mono value={fmt$(customMarginTotal)} />}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                          marginTop: 8, paddingTop: 8, borderTop: '0.5px solid #E0E0DC',
                          fontSize: 11, fontWeight: 600 }}>
                          <span style={{ color: '#111' }}>Total profit</span>
                          <span style={{ fontFamily: 'monospace', color: '#1A5C38' }}>{fmt$(itTotalProfit)}</span>
                        </div>
                      </div>
                    );

                    return (
                      <tr key={it.id} style={{ background: rowBg }}>
                        {/* PRODUCT — combined name + SKU, sticky-left, hover for profit breakdown */}
                        <td style={Object.assign({}, td, cellWidth(W.product), stickyLeft(0, rowBg),
                          { cursor: 'pointer', overflow: 'hidden' }, firstCellRadius || {})}
                          onClick={function() { jumpTo('items', it.id); }}>
                          <LedgerHoverTooltip content={productTooltip} position="left" width={320}>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                                <div style={{ fontSize: 12, color: '#111', fontWeight: 500,
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>
                                  {it.productName || it.item || '(unnamed)'}
                                </div>
                                <LedgerCoBadge changeOrders={st.changeOrders} />
                              </div>
                              <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#888', marginTop: 2 }}>
                                {it.itemNumber || '\u2014'}
                              </div>
                            </div>
                          </LedgerHoverTooltip>
                        </td>

                        {/* CLIENT DEPOSIT */}
                        <td style={Object.assign({}, td, cellWidth(W.deposit), { background: rowBg })}>
                          <AmountCell
                            amount={depAmount} state={depSt} partialOf={depPartialOf}
                            align="right" tooltip={depTooltip} />
                        </td>

                        {/* CLIENT FINAL */}
                        <td style={Object.assign({}, td, cellWidth(W.final), { background: rowBg })}>
                          <AmountCell
                            amount={finAmount} state={finSt} partialOf={finPartialOf}
                            align="right" tooltip={finTooltip} />
                        </td>

                        {/* VENDOR PO */}
                        <td style={Object.assign({}, td, cellWidth(W.po), { background: rowBg })}>
                          <AmountCell
                            amount={poAmount} state={poSt}
                            align="right" tooltip={poTooltip} />
                        </td>

                        {/* VENDOR FINAL */}
                        <td style={Object.assign({}, td, cellWidth(W.venFinal), { background: rowBg })}>
                          <AmountCell
                            amount={venFinAmount} state={venSt}
                            align="right" tooltip={venFinTooltip} />
                        </td>

                        {/* DELIVERED — date or em-dash, no color cue */}
                        <td style={Object.assign({}, td, cellWidth(W.delivered), { background: rowBg })}>
                          {st.receivedDate ? (
                            <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#111' }}>
                              {fmtShortDate(st.receivedDate)}
                            </span>
                          ) : (
                            <span style={{ color: '#bbb', fontFamily: 'monospace', fontSize: 14 }}>{'\u2014'}</span>
                          )}
                        </td>

                        {/* FEES */}
                        <td style={Object.assign({}, td, cellWidth(W.fees), { background: rowBg, textAlign: 'right' })}>
                          <FeesCell item={it} total={st.itemFees} />
                        </td>

                        {/* RECEIVABLE — sticky-right, color-cued amount */}
                        <td style={Object.assign({}, td, cellWidth(W.cliOut), stickyRight(W.venOut, rowBg),
                          { textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, fontSize: 13,
                            color: st.clientOutstanding > 0 ? '#D4007A' : '#888' })}>
                          {fmt$(st.clientOutstanding)}
                        </td>

                        {/* PAYABLE — sticky-right */}
                        <td style={Object.assign({}, td, cellWidth(W.venOut), stickyRight(0, rowBg),
                          { textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, fontSize: 13,
                            color: st.vendorOutstanding > 0 ? '#BF6800' : '#888' }, lastCellRadius || {})}>
                          {fmt$(st.vendorOutstanding)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}

        {/* Sticky-bottom totals row — separate table, scoped to current filter */}
        {groups.length > 0 && (
          <div style={{
            position: 'sticky', bottom: 0, zIndex: 3,
            marginTop: 14,
            background: FOOTER_BG, borderTop: '0.5px solid #D8D7D2',
          }}>
            <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: 'max-content' }}>
              <ColGroup />
              <tbody>
                <tr>
                  <td style={Object.assign({}, td, cellWidth(W.product), stickyLeft(0, FOOTER_BG),
                    { background: FOOTER_BG, fontWeight: 600 })}>
                    Totals
                    <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                      Net <span style={{ fontFamily: 'monospace', color: '#111' }}>{fmt$(totals.net)}</span> · Client <span style={{ fontFamily: 'monospace', color: '#111' }}>{fmt$(totals.clientTotal)}</span>
                    </div>
                    <div style={{ fontSize: 10, color: '#888', marginTop: 1 }}>
                      Profit <span style={{ fontFamily: 'monospace', color: '#1A5C38' }}>{fmt$(totals.profit)}</span>
                      <span style={{ color: '#aaa' }}>{' (' + (totals.net > 0 ? Math.round((totals.profit / totals.net) * 100) : 0) + '%)'}</span>
                    </div>
                  </td>
                  <td style={Object.assign({}, td, cellWidth(W.deposit), { background: FOOTER_BG })} />
                  <td style={Object.assign({}, td, cellWidth(W.final), { background: FOOTER_BG })} />
                  <td style={Object.assign({}, td, cellWidth(W.po), { background: FOOTER_BG })} />
                  <td style={Object.assign({}, td, cellWidth(W.venFinal), { background: FOOTER_BG })} />
                  <td style={Object.assign({}, td, cellWidth(W.delivered), { background: FOOTER_BG })} />
                  <td style={Object.assign({}, td, cellWidth(W.fees), { background: FOOTER_BG })} />
                  <td style={Object.assign({}, td, cellWidth(W.cliOut), stickyRight(W.venOut, FOOTER_BG),
                    { textAlign: 'right', background: FOOTER_BG, zIndex: 4 })}>
                    <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13,
                      color: totals.clientOutstanding > 0 ? '#D4007A' : '#888' }}>
                      {fmt$(totals.clientOutstanding)}
                    </div>
                    <div style={{ fontSize: 8, fontWeight: 600, color: '#888',
                      textTransform: 'uppercase', letterSpacing: '0.07em', marginTop: 1 }}>Receivable</div>
                  </td>
                  <td style={Object.assign({}, td, cellWidth(W.venOut), stickyRight(0, FOOTER_BG),
                    { textAlign: 'right', background: FOOTER_BG, zIndex: 4 })}>
                    <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13,
                      color: totals.vendorOutstanding > 0 ? '#BF6800' : '#888' }}>
                      {fmt$(totals.vendorOutstanding)}
                    </div>
                    <div style={{ fontSize: 8, fontWeight: 600, color: '#888',
                      textTransform: 'uppercase', letterSpacing: '0.07em', marginTop: 1 }}>Payable</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ fontSize: 11, color: '#888', marginTop: 10, lineHeight: 1.6 }}>
        Each cell shows <strong>this item's specific share</strong> of the lifecycle stage. Amount color cues
        the state: <span style={{ color: '#1A5C38' }}>green = paid</span>, <span style={{ color: '#BF6800' }}>amber = partial</span>, <span style={{ color: '#D4007A' }}>fuchsia = unpaid</span>, <span style={{ color: '#888' }}>muted = projected</span>.
        Hover any cell for ref / dates. Click the SKU/name to open the item.
      </div>

    </div>
  );
}
